import { LessonCard, type LessonItem } from "@/components/LessonCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { useZone } from "@/contexts/ZoneContext";
import { australianCities, regions } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, GraduationCap, MapPin } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";

// Phase 3.3: 과목 카테고리
const subjects = [
  { id: "all", label: "전체" },
  { id: "math", label: "수학" },
  { id: "english", label: "영어" },
  { id: "science", label: "과학" },
  { id: "coding", label: "코딩" },
  { id: "music", label: "음악" },
  { id: "art", label: "예체능" },
];

// Phase 3.3: 레벨
const levels = [
  { id: "all", label: "전체" },
  { id: "elementary", label: "초등" },
  { id: "middle", label: "중등" },
  { id: "high", label: "고등" },
  { id: "adult", label: "성인" },
];

export default function Lessons() {
  const { user } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();
  const { selectedZoneId } = useZone();
  const [selectedState, setSelectedState] = useState<string>(
    selectedRegion || "ALL"
  );
  const [selectedCity, setSelectedCity] = useState<string>("ALL");
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Phase 3.3: 과외/학원 전용 필터
  const [selectedSubject, setSelectedSubject] = useState<string>("all");
  const [selectedLevel, setSelectedLevel] = useState<string>("all");
  const [format, setFormat] = useState<string>("all"); // all, in-person, online, hybrid
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");

  // Phase 3.3: 과외/학원 목록 조회 (category: 'tutoring' 필터)
  const { data: listings, isLoading } = useQuery<LessonItem[]>({
    queryKey: ["/api/unified-market", selectedZoneId, selectedState, selectedCity, "tutoring"],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("category", "tutoring");
      params.append("type", "ad");
      params.append("zone", selectedZoneId);
      if (selectedState !== "ALL") params.append("state", selectedState);
      if (selectedCity !== "ALL") params.append("city", selectedCity);

      const res = await fetch(`/api/unified-market?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch listings");
      const data = await res.json();
      // 응답이 { ok: true, data: { items: [...] } } 형식일 수 있음
      if (data?.ok && data?.data?.items) {
        return data.data.items;
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
  });

  // Phase 3.3: 고급 필터링 로직
  const filteredListings = useMemo(() => {
    if (!listings) return [];

    let result = listings.filter((listing) => {
      // 검색어 필터
      if (
        searchTerm &&
        !listing.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !listing.description?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // 과목 필터
      if (selectedSubject !== "all" && listing.subject !== selectedSubject) {
        return false;
      }

      // 레벨 필터
      if (selectedLevel !== "all" && listing.level !== selectedLevel) {
        return false;
      }

      // 형식 필터 (대면/온라인)
      if (format !== "all" && listing.format !== format) {
        return false;
      }

      // 가격범위 필터
      if (minPrice || maxPrice) {
        const price = listing.priceCents;
        if (
          price === null ||
          price === undefined ||
          price === -1 ||
          price === 0
        ) {
          // 가격협의는 필터에서 제외하지 않음
        } else {
          const priceInDollars = price / 100;
          if (minPrice && priceInDollars < parseFloat(minPrice)) return false;
          if (maxPrice && priceInDollars > parseFloat(maxPrice)) return false;
        }
      }

      return true;
    });

    // 최신순 정렬
    return result.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [
    listings,
    searchTerm,
    selectedSubject,
    selectedLevel,
    format,
    minPrice,
    maxPrice,
  ]);

  const stateRegion = regions.find((r) => r.id === selectedState);
  const availableCities =
    selectedState !== "ALL" && selectedState in australianCities
      ? australianCities[selectedState as keyof typeof australianCities]
      : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3" data-testid="text-title">
              과외/학원
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              수학, 영어, 코딩, 음악, 예체능 - 호주 한인 커뮤니티 과외 및 학원
              정보
            </p>
            <div className="flex gap-4 justify-center items-center flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <GraduationCap className="w-3 h-3" />
                과외/학원
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <MapPin className="w-3 h-3" />
                지역기반
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <BookOpen className="w-3 h-3" />
                인증 강사
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <aside className="lg:col-span-1">
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">검색</label>
                  <Input
                    placeholder="제목 또는 설명 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="input-search"
                  />
                </div>

                {/* State Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주/지역
                  </label>
                  <Select
                    value={selectedState}
                    onValueChange={(value) => {
                      setSelectedState(value);
                      setSelectedCity("ALL");
                    }}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="주 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">전체 지역</SelectItem>
                      {regions
                        .filter((r) => r.id !== "ALL")
                        .map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter */}
                {availableCities.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      도시
                    </label>
                    <Select
                      value={selectedCity}
                      onValueChange={setSelectedCity}
                    >
                      <SelectTrigger data-testid="select-city">
                        <SelectValue placeholder="도시 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">전체 도시</SelectItem>
                        {availableCities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Phase 3.3: 과목 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">과목</label>
                  <Select
                    value={selectedSubject}
                    onValueChange={setSelectedSubject}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {subjects.map((subject) => (
                        <SelectItem key={subject.id} value={subject.id}>
                          {subject.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.3: 레벨 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">레벨</label>
                  <Select
                    value={selectedLevel}
                    onValueChange={setSelectedLevel}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((level) => (
                        <SelectItem key={level.id} value={level.id}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.3: 형식 필터 (대면/온라인) */}
                <div>
                  <label className="text-sm font-medium mb-2 block">형식</label>
                  <Select value={format} onValueChange={setFormat}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">전체</SelectItem>
                      <SelectItem value="in-person">대면</SelectItem>
                      <SelectItem value="online">온라인</SelectItem>
                      <SelectItem value="hybrid">혼합</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.3: 가격범위 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    가격범위 (시간당)
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Post Button */}
                {user && (
                  <Link href="/lessons/register">
                    <Button className="w-full" data-testid="button-register">
                      과외/학원 등록
                    </Button>
                  </Link>
                )}
                {!user && (
                  <Button className="w-full" variant="outline" disabled>
                    로그인 후 등록 가능
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Main Content - Listings */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-4 flex justify-between items-center">
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-count"
              >
                {filteredListings
                  ? `${filteredListings.length}개의 과외/학원`
                  : "로딩 중..."}
              </p>
            </div>

            {/* Listings Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredListings && filteredListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredListings.map((listing) => (
                  <LessonCard
                    key={listing.id}
                    item={listing}
                    onInquire={() => {
                      // TODO: 채팅 시작 로직
                      window.location.href = `/chat-start/lessons/${listing.id}`;
                    }}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <GraduationCap className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-lg font-medium mb-2">
                    등록된 과외/학원이 없습니다
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    첫 번째 과외/학원을 등록해보세요!
                  </p>
                  {user && (
                    <Link href="/lessons/register">
                      <Button>과외/학원 등록하기</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
